import { AgGridReact } from 'ag-grid-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import 'ag-grid-enterprise'
import { useSelector } from 'react-redux'
import { getLocaleText } from '../../actions/datagrid'
import { groupsGetAll, personsGetActive, personsTeamsSummaryGet, salaryHistoryGetAll } from '../../api'
import {
	contextMenu,
	customChartThemes,
	defaultColDef,
	gridSettings,
	loadViewState,
	restoreViewState,
	saveViewState,
	sideBar,
	statusBar,
} from '../../common/grid'
import { restoreIconString } from '../../common/icons'
import { PdfReport } from '../../common/pdfreport'
import { dateFormatter, dateGetter, localized, numberFormatter, numberFormatterFraction } from '../../common/utils'
import { authSelector } from '../../reducers'

const initialState = {
	columnVisibility: {
		hiddenColIds: [
			'memo',
			'experienceCode',
			'currency',
			'paymentPercentage',
			'paymentPercentageSalary',
			'phoneBenefit',
			'phoneBenefitAmount',
			'bicycleBenefit',
			'bicycleBenefitAmount',
			'mealBenefit',
			'mealBenefitAmount',
			'mealBenefitTaxableValue',
			'carUsageBenefit',
			'freeCarBenefit',
			'carBenefitYearModel',
			'carBenefitEmissionValue',
			'personalBenefit1',
			'personalBenefit2',
			...Array.from({ length: 16 }, (_, i) => `supplement${i + 1}`), // supplement1 - supplement16
			...Array.from({ length: 4 }, (_, i) => `annualSupplement${i + 1}`), // annualSupplement1 - annualSupplement4
			...Array.from({ length: 4 }, (_, i) => `fixedAnnualSupplement${i + 1}`), // fixedAnnualSupplement1 - fixedAnnualSupplement4
			'otherBenefits',
			'payUnit',
			'period',
			'day',
			'basis',
			'otherBasis',
			'dailyWorkHours',
			'weeklyWorkHours',
			'workHours',
			'workCycle',
			'workHoursInformation',
			'vacationInformation',
			'status',
			'createdBy',
			'createdAt',
			'statusChangedBy',
			'statusChangedAt',
			'jobTitle',
			'payGroup',
			'team',
		],
	},
}

export function SalaryHistoryTab() {
	const { t } = useTranslation()
	const { supplements } = useSelector(authSelector)
	const viewStateKey = 'salaryHistoryViewState'

	const gridRef = useRef()
	const [report, setReport] = useState(null)
	const closeReport = () => setReport(null)

	const [salaryHistory, setSalaryHistory] = useState([])

	const fetchData = useCallback(async () => {
		const [salaryRes, personsRes, personsTeamsSummaryRes, groupsRes] = await Promise.all([
			salaryHistoryGetAll(),
			personsGetActive(),
			personsTeamsSummaryGet(),
			groupsGetAll(),
		])
		const personsMap = new Map(personsRes.data.map((p) => [p.personId, p]))
		const teamsByPersonId = new Map(personsTeamsSummaryRes.data.map((p) => [p.personId, p.assignedTeams ?? []]))
		const groupsMap = new Map((groupsRes.data || []).map((g) => [g.id, g.name]))

		const rows = salaryRes.data
			.map((item, idx) => {
				const person = personsMap.get(item.personId)

				if (!person) return null

				const assignedTeams = teamsByPersonId.get(item.personId) ?? []

				return {
					id: `${item.personId}-${idx}`,
					...item,
					name: person.name,
					nr: person.nr,
					jobTitle: person.employmentDetails?.profession || '',
					payGroup: person.controlDetails?.payGroup || '',
					employeeGroup: groupsMap.get(person.controlDetails?.employeeGroupId) || '',
					team: assignedTeams.map((team) => team.name).join(', ') || '',
				}
			})
			.filter(Boolean)

		setSalaryHistory(rows)
	}, [])

	useEffect(() => {
		fetchData()
	}, [fetchData])

	const booleanFilterValueGetter = (params) => (params.value ? t('Yes', 'Kyllä') : t('No', 'Ei'))

	const columnDefs = [
		{
			field: 'nr',
			headerName: t('NumberAbbr', 'Nro'),
			width: 120,
			filter: 'agNumberColumnFilter',
		},
		{
			field: 'name',
			headerName: t('Name', 'Nimi'),
			filter: 'agTextColumnFilter',
		},
		{
			field: 'memo',
			headerName: t('Memo', 'Muistio'),
		},
		{
			field: 'experienceCode',
			headerName: t('ExperienceCode', 'Kokemuskoodi'),
			filter: 'agTextColumnFilter',
		},
		{
			field: 'salaryValidFrom',
			headerName: t('ValidFrom', 'Palkkatietojen voimassaolon alkupvm.'),
			filter: 'agDateColumnFilter',
			valueGetter: dateGetter,
			valueFormatter: dateFormatter,
			sort: 'desc',
		},
		{
			field: 'salaryValidUntil',
			headerName: t('ValidUntil', 'Palkkatietojen voimassaolon päättymispvm.'),
			filter: 'agDateColumnFilter',
			valueGetter: dateGetter,
			valueFormatter: dateFormatter,
		},
		{
			field: 'monthlyWage',
			headerName: t('MonthlyWage', 'Kuukausipalkka'),
			filter: 'agNumberColumnFilter',
			valueFormatter: numberFormatterFraction,
		},
		{
			field: 'hourlyWageATP',
			headerName: t('HourlyWageATP', 'Aikatuntipalkka'),
			filter: 'agNumberColumnFilter',
			valueFormatter: numberFormatterFraction,
		},
		{
			field: 'hourlyWageOTP',
			headerName: t('HourlyWageOTP', 'Ohjetuntipalkka'),
			filter: 'agNumberColumnFilter',
			valueFormatter: numberFormatterFraction,
		},
		{
			field: 'hourlyWagePTP',
			headerName: t('HourlyWagePTP', 'Perustuntipalkka'),
			filter: 'agNumberColumnFilter',
			valueFormatter: numberFormatterFraction,
		},
		{
			field: 'currency',
			headerName: t('Currency', 'Valuutta'),
			width: 100,
			valueGetter: ({ data }) => data?.currency || 'EUR',
		},
		{
			field: 'employmentType',
			headerName: t('EmploymentType', 'Palvelussuhteen tyyppi'),
			filter: 'agSetColumnFilter',
			valueFormatter: ({ value }) => localized.employmentTypes[value] || value,
		},
		{
			field: 'formOfSalary',
			headerName: t('FormOfSalary', 'Palkkausmuoto'),
			filter: 'agSetColumnFilter',
			valueFormatter: ({ value }) => localized.salaryForms[value] || value,
		},
		{
			field: 'partTimePercentage',
			headerName: t('PartTimePercentage', 'Osa-aikaprosentti'),
			filter: 'agNumberColumnFilter',
			valueFormatter: (params) => {
				const val = numberFormatterFraction(params)
				return val ? `${val} %` : val
			},
		},
		{
			field: 'paymentPercentage',
			headerName: t('PaymentCoefficient', 'Maksukerroin'),
			filter: 'agNumberColumnFilter',
			valueFormatter: numberFormatterFraction,
		},
		{
			field: 'paymentPercentageSalary',
			headerName: t('PaymentPercentageSalary', 'Maksukertoimen mukainen palkka'),
			filter: 'agNumberColumnFilter',
			valueFormatter: numberFormatterFraction,
		},
		{
			field: 'phoneBenefit',
			headerName: t('PhoneBenefit', 'Puhelinetu'),
			filter: 'agSetColumnFilter',
			cellRenderer: booleanFilterValueGetter,
			filterValueGetter: booleanFilterValueGetter,
		},
		{
			field: 'phoneBenefitAmount',
			headerName: t('PhoneBenefitAmount', 'Puhelinedun arvo'),
			filter: 'agNumberColumnFilter',
		},
		{
			field: 'bicycleBenefit',
			headerName: t('BicycleBenefit', 'Polkupyöräetu'),
			cellRenderer: booleanFilterValueGetter,
			filterValueGetter: booleanFilterValueGetter,
		},
		{
			field: 'bicycleBenefitAmount',
			headerName: t('BicycleBenefitAmount', 'Polkupyöräedun arvo'),
			filter: 'agNumberColumnFilter',
		},
		{
			field: 'mealBenefit',
			headerName: t('MealBenefit', 'Ateriaetu'),
			filter: 'agSetColumnFilter',
			cellRenderer: booleanFilterValueGetter,
			filterValueGetter: booleanFilterValueGetter,
		},

		{
			field: 'mealBenefitAmount',
			headerName: t('MealBenefitAmount', 'Ateriaedun arvo'),
			filter: 'agNumberColumnFilter',
		},

		{
			field: 'mealBenefitTaxableValue',
			headerName: t('MatchTaxValue', 'Vastaa verotusarvoa'),
			cellRenderer: booleanFilterValueGetter,
			filterValueGetter: booleanFilterValueGetter,
		},

		{
			field: 'carUsageBenefit',
			headerName: t('LimitedCarBenefit', 'Käyttöetu'),
			cellRenderer: booleanFilterValueGetter,
			filterValueGetter: booleanFilterValueGetter,
		},
		{
			field: 'freeCarBenefit',
			headerName: t('FullCarBenefit', 'Vapaa autoetu'),
			cellRenderer: booleanFilterValueGetter,
			filterValueGetter: booleanFilterValueGetter,
		},
		{
			field: 'carBenefitYearModel',
			headerName: t('CarBenefitYearModel', 'Autoetu vuosimalli'),
		},
		{
			field: 'carBenefitEmissionValue',
			headerName: t('CarBenefitEmissionValue', 'Auton CO2-arvo'),
		},
		{
			field: 'personalBenefit1',
			headerName: t('PersonalBenefit1', 'Henk.koht luontoisetu'),
		},

		{
			field: 'personalBenefit2',
			headerName: t('PersonalBenefit2', 'Henkilöetu 2'),
		},

		...Array.from({ length: 16 }, (_, i) => ({
			field: `supplement${i + 1}`,
			headerName: supplements[i] || `${t('Supplement', 'Lisä')} ${i + 1}`,
			valueFormatter: numberFormatter,
		})),

		...Array.from({ length: 4 }, (_, i) => ({
			field: `annualSupplement${i + 1}`,
			headerName: `${t('AnnualSupplement', 'Vuosisidonnainen lisä')} ${i + 1}`,
			valueFormatter: numberFormatter,
		})),

		...Array.from({ length: 4 }, (_, i) => ({
			field: `fixedAnnualSupplement${i + 1}`,
			headerName: `${t('FixedAnnualSupplement', 'Määrävuosilisä')} ${i + 1}`,
			valueFormatter: numberFormatter,
		})),

		{
			field: 'otherBenefits',
			headerName: t('OtherBenefitsAndSalaryMatters', 'Muut luontoisedut ja palkkaan liittyvät asiat'),
		},
		{
			field: 'payUnit',
			headerName: t('PayUnit', 'Palkkayksikkö'),
		},
		{
			field: 'period',
			headerName: t('PaymentPeriod', 'Maksukausi'),
		},
		{
			field: 'day',
			headerName: t('Day', 'Maksupäivä'),
		},
		{
			field: 'basis',
			headerName: t('Basis', 'Palkan perusteet'),
		},
		{
			field: 'otherBasis',
			headerName: t('OtherBasis', 'Muu peruste'),
		},
		{
			field: 'dailyWorkHours',
			headerName: t('DailyHours', 'Vuorokautinen työaika'),
			valueFormatter: numberFormatter,
		},
		{
			field: 'weeklyWorkHours',
			headerName: t('WeeklyWorkHours', 'Viikkotunnit'),
			valueFormatter: numberFormatterFraction,
		},
		{
			field: 'workHours',
			headerName: t('Hours', 'Tuntia'),
		},
		{
			field: 'workCycle',
			headerName: t('WorkCycle', 'Työkierto'),
		},
		{
			field: 'workHoursInformation',
			headerName: t('WorkHoursInformation', 'Työaikatiedot'),
		},
		{
			field: 'vacationInformation',
			headerName: t('VacationInformation', 'Lomatiedot'),
		},
		{
			field: 'createdBy',
			headerName: t('CreatedBy', 'Luonut'),
		},
		{
			field: 'createdAt',
			headerName: t('CreatedAt', 'Luotu'),
			filter: 'agDateColumnFilter',
			valueGetter: dateGetter,
			valueFormatter: dateFormatter,
		},
		{
			field: 'status',
			headerName: t('Status', 'Tila'),
		},
		{
			field: 'statusChangedBy',
			headerName: t('StatusChangedBy', 'Tilaa muutti'),
		},
		{
			field: 'statusChangedAt',
			headerName: t('StatusChangedAt', 'Tila muutettu'),
			filter: 'agDateColumnFilter',
			valueGetter: dateGetter,
			valueFormatter: dateFormatter,
		},
		{
			field: 'jobTitle',
			headerName: t('JobTitle', 'Tehtävänimike'),
			filter: 'agTextColumnFilter',
		},
		{
			field: 'payGroup',
			headerName: t('PayGroup', 'Maksuryhmä'),
			filter: 'agTextColumnFilter',
		},
		{
			field: 'employeeGroup',
			headerName: t('EmployeeGroup', 'Työntekijäryhmä'),
			filter: 'agTextColumnFilter',
		},
		{
			field: 'team',
			headerName: t('Team', 'Tiimi'),
			filter: 'agTextColumnFilter',
		},
	]

	const getContextMenuItems = useCallback(
		(params) => {
			let items = []

			items.push({
				name: t('DefaultGridValues', 'Palauta taulukon oletusasetukset'),
				action: () => {
					restoreViewState(viewStateKey, initialState)
				},
				icon: restoreIconString,
			})

			items.push('separator')

			items = items.concat(
				contextMenu(t, params, setReport, {
					title: t('SalaryHistory', 'Palkkahistoria'),
				}),
			)

			return items
		},
		[t],
	)

	return (
		<>
			{report !== null && <PdfReport json={report} onCloseReport={closeReport} />}
			<h2 className="viewheading">{t('SalaryHistory', 'Palkkahistoria')}</h2>

			<Row className="ag-theme-material styles-ag mt-3 flex-grow-1">
				<AgGridReact
					{...gridSettings}
					ref={gridRef}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					customChartThemes={customChartThemes}
					chartThemes={['chartTheme']}
					sideBar={sideBar}
					statusBar={statusBar}
					rowData={salaryHistory}
					getContextMenuItems={getContextMenuItems}
					getLocaleText={(params) => getLocaleText(t, params)}
					animateRows
					suppressCellFocus
					enableCellTextSelection
					initialState={loadViewState(viewStateKey) || initialState}
					onStateUpdated={({ state }) => saveViewState(state, viewStateKey)}
					debug={import.meta.env.DEV}
				/>
			</Row>
		</>
	)
}
